import { createContext, createProgram, type Mesh } from "./webgl";
import { cubeVS, cubeFS, ghostFS } from "./shaders";
import { pick } from "./render/picking";
import { frameCube } from "./camera";
import { StepInput, type Step } from "./input";
import { loadTexture, ModelRenderer, poseWorlds, type CookedModel } from "./model";
import { BugRenderer, placeOnSurface } from "./render/bug-renderer";
import { SpawnerRenderer } from "./render/spawner-renderer";
import { PowerUpRenderer } from "./render/powerup-renderer";
import { loadAllModels, type ModelName } from "./render/model-catalog";
import { MenuRenderer, hitTestRects, type MenuScreenRect } from "./render/menu-renderer";
import { toScreen as projectToScreen } from "./render/view";
import { stepWorld, drainAccumulator, SIM_DT, type World } from "./game/world";
import { AudioEngine, createWebAudioBackend } from "./audio/engine";
import { AudioDirector, type AudioLogEntry } from "./audio/director";
import { ALL_AUDIO_URLS } from "./audio/banks";
import { mulberry32 } from "./game/rng";
import { createSession, type Session } from "./shell/session";
import { Shell, type MenuPage } from "./shell/shell";
import { DEMO_LEVELS, DIFFICULTIES, POWERUP_DEFS, POWERUP_NAMES, type DifficultyDef } from "./game/defs";
import { isCollectable, powerUpFrame } from "./game/powerups";
import { bugFrame, type BugKind } from "./game/bugs";
import { FACE_IDS, N, poseToCell, poseToWorld, type SurfacePose, type Vec3 } from "./game/surface";
import {
  identity,
  perspective,
  multiply,
  rotationX,
  rotationY,
  translation,
  type Mat4,
} from "./math";

const canvas = document.getElementById("gl") as HTMLCanvasElement;
const fadeEl = document.getElementById("fade") as HTMLElement;
const gl = createContext(canvas);

const program = createProgram(gl, cubeVS, cubeFS);
const uMvp = gl.getUniformLocation(program, "uMvp");
const uModel = gl.getUniformLocation(program, "uModel");
gl.useProgram(program);
gl.uniform1i(gl.getUniformLocation(program, "uAtlas"), 0);

// Same vertex shader and attribute layout, untextured translucent fragment.
const ghostProgram = createProgram(gl, cubeVS, ghostFS);
const uGhostMvp = gl.getUniformLocation(ghostProgram, "uMvp");
const uGhostModel = gl.getUniformLocation(ghostProgram, "uModel");

// Original block texture atlas; a flat green placeholder shows until it loads.
const atlasTex = gl.createTexture();
if (!atlasTex) throw new Error("Failed to create texture");
gl.bindTexture(gl.TEXTURE_2D, atlasTex);
gl.texImage2D(
  gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
  new Uint8Array([70, 120, 45, 255]),
);
gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
loadTexture(gl, new URL("../assets/textures/subblock_1536_tex.png", import.meta.url), atlasTex)
  .catch((err) => console.error("failed to load block atlas:", err));

const input = new StepInput(canvas);

// Audio (plan 18): the engine owns Web Audio, the director subscribes to the
// current session's event bus. The AudioContext is created lazily on the
// first genuine gesture (browser autoplay policy) — until then every play
// call is a cheap no-op that still lands in the director's log, which is what
// keeps the headless `?debug=` fleet silent-safe. The seed is salted so the
// director's clip picks draw from their own stream and can never perturb the
// sim's (the plan's `0xaud10` is not valid hex; `0xa0d10` is the closest
// legal spelling).
const audioBackend = createWebAudioBackend();
const audio = new AudioEngine(audioBackend);
audioBackend.warm(ALL_AUDIO_URLS);
const director = new AudioDirector(
  audio,
  mulberry32((Date.now() >>> 0) ^ 0xa0d10),
  () => performance.now() / 1000,
);
canvas.addEventListener("pointerdown", () => audio.unlock());
window.addEventListener("keydown", () => audio.unlock());

// The whole model roster, cooked from the original FBXs by tools/cook_models.sh;
// run the cook + texture scripts if these assets are missing. One loading gate:
// nothing draws models until every one of them is on the GPU.
const modelRenderer = new ModelRenderer(gl);
let models: Record<ModelName, CookedModel> | null = null;
let bugRenderer: BugRenderer | null = null;
let spawnerRenderer: SpawnerRenderer | null = null;
let powerUpRenderer: PowerUpRenderer | null = null;
let menuRenderer: MenuRenderer | null = null;
loadAllModels(gl).then(
  (loaded) => {
    models = loaded;
    bugRenderer = new BugRenderer(loaded);
    spawnerRenderer = new SpawnerRenderer(loaded.apple);
    powerUpRenderer = new PowerUpRenderer(loaded);
    menuRenderer = new MenuRenderer(loaded);
  },
  (err) => console.error("failed to load models:", err),
);

// Boot routing (plan 17): the default page boots to the MENU. Every `?debug=`
// scenario skips it and lands straight in a running world (easy difficulty,
// main table) so the shot fleet keeps working — except `?debug=menu`, which
// boots the menu *with* instrumentation (`buggsDebug.menu()`).
const debug = new URLSearchParams(location.search).get("debug");
// `?debug=roster` swaps the demo population for one bug of every kind, spread
// over the front and top faces, plus a standing apple — the visual check that
// each kind draws with its own model (tools/shot-roster.mjs).
const roster = debug === "roster";
// `?debug=collision` crowds six bugs onto the front face on courses that cross,
// the visual check that they deflect rather than overlap (tools/shot-collision.mjs).
const collision = debug === "collision";
// `?debug=behaviours` is plan 14's check: four kinds on four parallel lanes of
// the front face, each doing its own thing to the cube as it walks
// (tools/shot-behaviours.mjs).
const behaviours = debug === "behaviours";
// `?debug=powerups` is plan 16 §1's check: all ten plants standing on the front
// face in slot order, view-only props like the roster apple — the visual audit
// of each model's skin, orientation, lift and idle cycle before any power-up
// logic exists (tools/shot-powerups.mjs).
const powerups = debug === "powerups";

// Level 1's two apples (`AmountOfSpawnPoints = 2`), on two different faces so
// the wandering is visible from the resting camera and the population spreads
// around the cube. Face ids are canonical (00-architecture.md §7).
const APPLE_SPAWNS: readonly SurfacePose[] = [
  { face: 4, u: 1.5, v: 3.5, dir: 3 }, // front, walking down towards -Y
  { face: 2, u: 3.5, v: 2.5, dir: 3 }, // top, walking towards the front edge
];

// All seven kinds walking, in two rows across the front face — the resting
// camera is face-on, so anything on another face is out of frame and could not
// be checked. They all walk down (dir 3) at their own speeds, which keeps the
// rows from piling into each other; plan 05 brings collision, until then they
// would simply pass through each other anyway.
const ROSTER_SPAWNS: readonly (readonly [BugKind, SurfacePose])[] = [
  ["standard", { face: 4, u: 0.6, v: 4.4, dir: 3 }],
  ["lady", { face: 4, u: 2, v: 4.4, dir: 3 }],
  ["fat", { face: 4, u: 3.4, v: 4.4, dir: 3 }],
  ["speed", { face: 4, u: 4.6, v: 4.4, dir: 3 }],
  ["spy", { face: 4, u: 1, v: 3, dir: 3 }],
  ["spike", { face: 4, u: 2.6, v: 3, dir: 3 }],
  ["black", { face: 4, u: 4.2, v: 3, dir: 3 }],
];

// View-only prop: no spawner logic yet (plan 06), just the apple model standing
// on the front face so the roster shot proves it loads and sits upright.
const ROSTER_APPLE: SurfacePose = { face: 4, u: 2.2, v: 0.9, dir: 3 };

// The ten plants in slot order, mushrooms (slots 0-4) across the upper row and
// flowers (slots 5-9) across the lower — view-only props like the roster
// apple; the sim has no power-up system yet (plan 16 §1 is assets only). The
// resting camera is face-on, so only the front face can be audited.
const POWERUP_SPAWNS: readonly SurfacePose[] = POWERUP_NAMES.map((_, slot) => ({
  face: 4,
  u: 0.5 + (slot % 5),
  v: slot < 5 ? 3.5 : 1.5,
  dir: 3,
}));

// Three pairs walking straight at each other down the middle three lanes of the
// front face — they meet around v = 2.5, in full view of the resting camera,
// and the deflections then push them into each other's lanes for a second round.
const COLLISION_SPAWNS: readonly SurfacePose[] = [
  { face: 4, u: 1.5, v: 4.5, dir: 3 },
  { face: 4, u: 1.5, v: 0.5, dir: 1 },
  { face: 4, u: 2.5, v: 4.5, dir: 3 },
  { face: 4, u: 2.5, v: 0.5, dir: 1 },
  { face: 4, u: 3.5, v: 4.5, dir: 3 },
  { face: 4, u: 3.5, v: 0.5, dir: 1 },
];

// Plan 14's roster behaviours, one kind per lane so their damage patterns can
// be compared side by side under the face-on resting camera. They all walk +u
// across the front face and never share a lane, so nothing here is a collision
// test. Top to bottom: spike (stops on *every* healthy block, so it chews a
// solid trail), standard (the same speed budget but a coin flip, so it leaves
// gaps), fat (its 8-star bite widens each stop into a blob), spy (walks three
// blocks marking them, then eats all three at once — from a standstill three
// blocks back, which is the giveaway).
const BEHAVIOUR_SPAWNS: readonly (readonly [BugKind, SurfacePose])[] = [
  ["spike", { face: 4, u: 0.5, v: 4.5, dir: 0 }],
  ["standard", { face: 4, u: 0.5, v: 3.5, dir: 0 }],
  ["fat", { face: 4, u: 0.5, v: 2.5, dir: 0 }],
  ["spy", { face: 4, u: 0.5, v: 0.5, dir: 0 }],
];

// `?debug=crater` digs a crater into the front face at startup — the visual
// check for plan 11's depth texturing (tools/shot-onion.mjs). Random decay is
// no good for this: over 90 s the apples infect plenty of blocks but almost
// never land the six bites one block needs to be eaten through, so the magma
// layer never gets uncovered. Digging it directly is deterministic and takes a
// frame. Cells are (x, y, z) with z = 4 the front face (00-architecture.md §7).
const CRATER_HOLES: readonly (readonly [number, number, number])[] = [
  // A 2x2 bite out of the crust: its floor is the depth-1 magma layer.
  [1, 3, 4],
  [2, 3, 4],
  [1, 2, 4],
  [2, 2, 4],
  // One column dug a layer deeper, to expose the molten core cell behind it.
  [3, 1, 4],
  [3, 1, 3],
];
// Left part-eaten, so the scorch tint sits next to the holes for comparison.
const CRATER_INFECTED: readonly (readonly [number, number, number])[] = [
  [0, 4, 4],
  [4, 4, 4],
  [0, 0, 4],
];

// Orientation is always a snapped axis-aligned matrix; a step animates a
// quarter-turn around the *world* X or Y axis on top of it, then bakes in.
// The shell's menu turns feed the same machinery a synthetic Step, with a
// completion callback so the page flips exactly when the turn lands.
const TURN_DURATION = 0.3; // seconds per quarter-turn
let base: Mat4 = identity();
let anim: { step: Step; t: number; done: (() => void) | null } | null = null;
let model: Mat4 = base; // model matrix of the last rendered frame, for picking

// The one place a quarter-turn begins — the in-game swipe/key queue and the
// shell's menu turns both land here, so one hook covers the rotate sound for
// game and menu, as in the original (`Cube.cs:3387`, `:3478-3536`).
function startTurn(step: Step, done: (() => void) | null): void {
  anim = { step, t: 0, done };
  director.cubeRotateStarted();
}

// The current run. Everything per-run (world, cube meshes, dirty-subscription)
// lives in the session so a menu-started game replaces it wholesale (plan 17).
let session: Session | null = null;

function addDefaultApples(world: World): void {
  for (const pose of APPLE_SPAWNS) world.spawners.add(pose);
}

const seed = (): number => Date.now() >>> 0;

// How long the DOM overlay's CSS opacity transition runs (index.html #fade);
// the port's `InOutFader`. The timeout adds a small margin so the callback
// never beats the transition's end.
const FADE_SECONDS = 0.4;

let shell: Shell | null = null;
if (debug === null || debug === "menu") {
  shell = new Shell({
    buildMenuSession: () => {
      // The unattended demo game behind the text: the demo ramp and the
      // standard two apples; no command is ever pushed into it. It decays
      // forever, as the original's menu cube does — nothing repairs it and
      // its gameLost/gameWon are ignored (nothing subscribes).
      session = createSession(gl, seed(), { levels: DEMO_LEVELS });
      addDefaultApples(session.world);
      // Summer.mp3 once over the demo game, as the menu scene boots
      // (`MainMenu.unity:8552`); the demo's bug SFX play, its block decay
      // stays silent (the director's in-game gate).
      director.attachMenu(session.world);
      return session;
    },
    buildGameSession: (difficulty: DifficultyDef) => {
      session = createSession(gl, seed(), { difficulty });
      addDefaultApples(session.world);
      director.attachGame(session.world, difficulty);
      return session;
    },
    rotate: (dir, done) => {
      startTurn({ axis: "y", dir }, done);
    },
    fadeOut: (done) => {
      fadeEl.style.opacity = "1";
      window.setTimeout(done, FADE_SECONDS * 1000 + 50);
    },
    fadeIn: () => {
      fadeEl.style.opacity = "0";
    },
    resetOrientation: () => {
      base = identity();
      anim = null;
    },
  });
} else {
  // Direct boot: the pre-17 behaviour every shot tool depends on — a running
  // world at easy difficulty on the main table, populated per scenario.
  const boot = createSession(gl, seed(), { difficulty: DIFFICULTIES[0] });
  session = boot;
  const world = boot.world;
  // Debug scenarios are running games, so they get the in-game mapping; with
  // no unlock gesture in a headless run the context stays suspended and the
  // director only logs.
  director.attachGame(world, DIFFICULTIES[0]);
  if (roster) {
    for (const [kind, pose] of ROSTER_SPAWNS) world.bugs.spawn(kind, pose);
  } else if (behaviours) {
    for (const [kind, pose] of BEHAVIOUR_SPAWNS) {
      // Start every lane hungry. `tilesUntilInfect` is normally a 1..19 draw, so
      // the slow fat bug would otherwise walk off the front face before its first
      // bite and the shot would miss its blob entirely. Zeroing it does not skip
      // any decision — the coin flip still runs per tile, which is exactly what
      // keeps the standard lane visibly patchier than the spike's.
      world.bugs.spawn(kind, pose).tilesUntilInfect = 0;
    }
  } else if (collision) {
    for (const pose of COLLISION_SPAWNS) world.bugs.spawn("standard", pose);
  } else if (powerups) {
    // Nothing to spawn: the plants are drawn as view-only props below, and an
    // apple population would walk bugs through the line-up mid-shot.
  } else {
    addDefaultApples(world);
  }

  if (debug === "crater") {
    // One touch infects; the block then decays to `ghost` on its own countdown,
    // so the holes appear INFECTED_SECONDS into the run rather than instantly.
    for (const [x, y, z] of CRATER_HOLES) world.blocks.infect(x, y, z);
    // Re-infected on a timer so the scorch tint is on screen when the shot is
    // taken — a single touch would have decayed away long before.
    const keepInfected = (): void => {
      for (const [x, y, z] of CRATER_INFECTED) world.blocks.infect(x, y, z);
    };
    keepInfected();
    setInterval(keepInfected, 200);
  }

  // `?debug=occupancy` dumps the tile claims once a second — the cheap window
  // into the collision system (the plan explicitly picks logging over shader work).
  if (debug === "occupancy") {
    let logged = 0;
    world.events.on("bugRemoved", ({ id }) => console.log(`bug ${id} removed, claims dropped`));
    setInterval(() => {
      const claims = [...world.bugs.occupancy.tiles].map(([tile, id]) => `${tile}:${id}`);
      // Population is the number plan 06 cares about: it must climb to the
      // level's MaxAmountOfBugs and hold there.
      console.log(
        `t=${(logged += 1)}s bugs=${world.bugs.bugs.size} apples=${world.spawners.spawners.size}` +
          ` claims [${claims.join(" ")}]`,
      );
    }, 1000);
  }

  if (debug === "interaction") {
    // The score/combo events have no HUD yet, so the log is where they show up.
    world.events.on("scored", (e) => console.info(`scored ${e.points} x${e.multiplier}`));
    world.events.on("bugHit", (e) => console.info(`bug ${e.id} hit ${e.hitsTaken}x, still alive`));
    world.events.on("combo", (e) => console.info(`combo of ${e.length}`));
    world.events.on("repairBlocked", (e) => console.info(`repair blocked at ${e.cell.join(",")}`));
    world.events.on("powerUpTaken", ({ id, slot, points }) => {
      console.info(`power-up ${id} taken slot=${slot} (${POWERUP_DEFS[slot].effect}) +${points}`);
    });
    globalThis.buggsDebug = {
      bugs: () => {
        const out: (ScreenTarget & { id: number; kind: BugKind })[] = [];
        for (const bug of world.bugs.bugs.values()) {
          if (bug.state.kind !== "running" && bug.state.kind !== "infecting") continue;
          const frame = bugFrame(bug);
          if (!facesCamera(frame.normal)) continue;
          const at = toScreen(frame.pos);
          if (at) out.push({ ...at, id: bug.id, kind: bug.kindDef });
        }
        return out;
      },
      damaged: () => {
        const out: (ScreenTarget & { cell: readonly [number, number, number] })[] = [];
        for (const face of FACE_IDS) {
          for (let v = 0; v < N; v++) {
            for (let u = 0; u < N; u++) {
              const pose: SurfacePose = { face, u: u + 0.5, v: v + 0.5, dir: 0 };
              const c = poseToCell(pose);
              if (world.blocks.state(c.x, c.y, c.z) === "healthy") continue;
              const frame = poseToWorld(pose);
              if (!facesCamera(frame.normal)) continue;
              const at = toScreen(frame.pos);
              if (at) out.push({ ...at, cell: [c.x, c.y, c.z] });
            }
          }
        }
        return out;
      },
      powerups: () => {
        const out: (ScreenTarget & { id: number; slot: number })[] = [];
        for (const p of world.powerups.powerups.values()) {
          if (!isCollectable(p.state)) continue;
          const frame = powerUpFrame(p);
          if (!facesCamera(frame.normal)) continue;
          const at = toScreen(frame.pos);
          if (at) out.push({ ...at, id: p.id, slot: p.slot });
        }
        return out;
      },
      spawnPowerUp: (slot: number) => {
        for (let v = 0; v < N; v++) {
          for (let u = 0; u < N; u++) {
            const placed = world.powerups.place(slot, 4, u, v);
            if (placed) return placed.id;
          }
        }
        return null;
      },
      audioLog: () => ({ locked: audio.locked, entries: director.audioLog() }),
    };
  }

  // The behaviours scenario has nothing to *see* for the spy's marks — the
  // original draws a `FieldMarker` prefab per mark and this port deliberately
  // draws nothing (plan 14, out of scope) — so the sim narrates them instead.
  // That, plus a line per bite, is what makes the shot readable: the trail on
  // screen shows what each kind ate, the log shows why.
  if (behaviours) {
    world.events.on("bugMarksChanged", ({ id, marks }) => {
      const kind = world.bugs.bugs.get(id)?.kindDef ?? "?";
      const where = marks.map((c) => `(${c.join(",")})`).join(" ");
      console.info(`t=${world.time.toFixed(1)}s ${kind} ${id} marks=${marks.length} ${where}`.trimEnd());
    });
    world.events.on("blockChanged", ({ x, y, z, state }) => {
      if (state === "infected") console.info(`t=${world.time.toFixed(1)}s bitten (${x},${y},${z})`);
    });
  }

  // `?debug=sim` reports the run's vital signs once a second: how much of the
  // cube is still healthy (plan 07, and what the ≤8% lose check reads), plus the
  // level, its speed factor and the per-kind population — the numbers that make
  // plan 15's difficulty ramp legible in `tools/shot-levels.mjs`.
  if (debug === "sim") {
    world.events.on("levelChanged", ({ level, def }) => {
      console.info(`t=${world.time.toFixed(1)}s level=${level} speedFactor=${def.speedFactor}`);
    });
    // Plan 16: power-up spawns, deaths-by-bug and collections, plus the active
    // modifiers in the 1 Hz line below — what makes the system legible in
    // `tools/shot-powerups.mjs`'s default-page run.
    world.events.on("powerUpSpawned", ({ id, slot }) => {
      console.info(
        `t=${world.time.toFixed(1)}s power-up ${id} spawned slot=${slot} (${POWERUP_DEFS[slot].effect})`,
      );
    });
    world.events.on("powerUpStateChanged", ({ id, state }) => {
      if (state === "killed") console.info(`t=${world.time.toFixed(1)}s power-up ${id} trampled by a bug`);
    });
    world.events.on("powerUpTaken", ({ id, slot, points }) => {
      console.info(
        `t=${world.time.toFixed(1)}s power-up ${id} taken slot=${slot} (${POWERUP_DEFS[slot].effect}) +${points}`,
      );
    });
    world.events.on("gameWon", ({ level }) => console.info(`t=${world.time.toFixed(1)}s GAME WON after level ${level}`));
    world.events.on("gameLost", ({ healthyFraction }) => {
      console.info(`t=${world.time.toFixed(1)}s GAME LOST at healthyFraction=${healthyFraction.toFixed(3)}`);
    });
    setInterval(() => {
      const healthy = world.blocks.healthyFraction().toFixed(3);
      const counts = new Map<BugKind, number>();
      for (const bug of world.bugs.bugs.values()) {
        counts.set(bug.kindDef, (counts.get(bug.kindDef) ?? 0) + 1);
      }
      const pop = [...counts].map(([kind, n]) => `${kind}:${n}`).join(",");
      const mods = world.modifiers;
      console.info(
        `t=${world.time.toFixed(1)}s level=${world.levels.level}` +
          ` speed=${world.levels.speedFactor.toFixed(2)} healthyFraction=${healthy}` +
          ` bugs=${world.bugs.bugs.size} [${pop}]` +
          ` powerups=${world.powerups.powerups.size}` +
          ` mods[x${mods.speedFactor} spawn x${mods.spawnRate}${mods.frozen ? " FROZEN" : ""}]`,
      );
    }, 1000);
  }
}

// `?debug=interaction` leaves the default population alone and only adds a
// window hook that reports where things are *on screen*, computed from sim
// state through the very matrix the last frame drew with. That is what lets
// `tools/shot-interaction.mjs` tap a specific bug or a specific damaged block
// headlessly (plan 08's definition of done) without the tool duplicating any
// of the projection math. `?debug=menu` gets the same treatment for the menu:
// `buggsDebug.menu()` reports the shell state and the active page's tappable
// rects — the very rects picking uses — so a tool can tap START without
// duplicating layout knowledge.
interface ScreenTarget {
  readonly x: number;
  readonly y: number;
}
interface BuggsDebug {
  bugs(): readonly (ScreenTarget & { readonly id: number; readonly kind: BugKind })[];
  damaged(): readonly (ScreenTarget & { readonly cell: readonly [number, number, number] })[];
  /** Collectable power-ups facing the camera, tappable like `bugs()` (plan 16). */
  powerups(): readonly (ScreenTarget & { readonly id: number; readonly slot: number })[];
  /**
   * Place a power-up of `slot` on the first free healthy front-face cell,
   * through the same `PowerUpManager.place` the spawn timer uses — how
   * `tools/shot-powerups.mjs` gets a repair power-up on screen without waiting
   * for the weights to roll one (the crater scenario's kind of shortcut).
   */
  spawnPowerUp(slot: number): number | null;
  audioLog(): AudioLogReport;
}
interface MenuDebug {
  menu(): {
    readonly state: string;
    readonly page: MenuPage | null;
    readonly items: readonly MenuScreenRect[];
  };
  audioLog(): AudioLogReport;
}
/**
 * What `tools/shot-audio.mjs` reads (plan 18): whether the context is still
 * suspended, and everything the director decided to play — recorded even
 * while locked, so the routing is checkable headlessly where nothing is
 * audible.
 */
interface AudioLogReport {
  readonly locked: boolean;
  readonly entries: readonly AudioLogEntry[];
}
declare global {
  var buggsDebug: BuggsDebug | MenuDebug | undefined;
}

if (debug === "menu" && shell !== null) {
  const liveShell = shell;
  globalThis.buggsDebug = {
    menu: () => {
      const page = liveShell.activePage;
      const rects =
        page !== null && menuRenderer !== null && anim === null
          ? menuRenderer.itemRects(page, model, canvas.getBoundingClientRect())
          : [];
      return { state: liveShell.state.kind, page, items: rects };
    },
    audioLog: () => ({ locked: audio.locked, entries: director.audioLog() }),
  };
}

/** Cube-local point to client-space pixels, or null if it is behind the camera. */
function toScreen(point: Vec3): ScreenTarget | null {
  return projectToScreen(point, model, canvas.getBoundingClientRect());
}

/**
 * True while a surface normal points back at the camera. The cube is convex, so
 * for a point on its surface that is the whole visibility test — no occlusion
 * to worry about beyond the bugs themselves.
 */
function facesCamera(normal: Vec3): boolean {
  const m = model;
  return m[2] * normal[0] + m[6] * normal[1] + m[10] * normal[2] > 0.3;
}

// The player's two verbs, plus the menu's one. In menu states a tap hit-tests
// the active page's item rects (nothing is tappable while a press animation or
// a turn runs); in `running` a tap picks against the sim exactly as before: a
// tap on a bug hurts it, a tap on a damaged block repairs it — through the
// hole if the ray went through one. Healthy grass does nothing, as in the
// original (`Cube.MouseInteractionWithObjects` breaks out of its hit list on
// the first Healthy block). Sim taps go through the command queue: the sim
// applies them at the top of the next fixed step.
input.onTap = ({ x, y }) => {
  if (session === null) return;
  if (shell !== null && !shell.tapsToSim) {
    const page = shell.activePage;
    if (page === null || anim !== null || menuRenderer === null) return;
    const rects = menuRenderer.itemRects(page, model, canvas.getBoundingClientRect());
    const item = hitTestRects(rects, x, y);
    if (item !== null) shell.tapItem(item);
    return;
  }
  const world = session.world;
  // `PickableWorld` is the narrow read-only view `picking.ts` needs; `World`
  // wraps its entities in managers, so hand over the maps themselves.
  const hit = pick(x, y, canvas, model, {
    blocks: world.blocks,
    bugs: world.bugs.bugs,
    powerups: world.powerups.powerups,
  });
  if (hit === null) return;
  if (hit.kind === "bug") {
    world.commands.push({ kind: "tapBug", id: hit.id });
    return;
  }
  if (hit.kind === "powerup") {
    world.commands.push({ kind: "tapPowerUp", id: hit.id });
    return;
  }
  // `pick` only ever returns a damaged cell — it stops at intact grass, as the
  // original's hit-list loop does — so there is nothing left to filter here.
  world.commands.push({ kind: "repairBlock", cell: hit.cell });
};

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// After a quarter-turn every entry is exactly -1, 0, or 1; rounding kills
// accumulated float drift so the resting cube is always perfectly face-on.
function snap(m: Mat4): Mat4 {
  const out = new Float32Array(16);
  for (let i = 0; i < 16; i++) out[i] = Math.round(m[i]);
  return out;
}

function currentModel(dt: number): Mat4 {
  if (!anim) {
    const step = input.next();
    // Swipes only rotate the cube while a game is running; in menu states a
    // dequeued step is simply dropped (the original ignores swipes in menu
    // mode). Shell-initiated turns arrive via `rotate` above, not this queue.
    if (step && (shell === null || shell.swipesToSim)) startTurn(step, null);
  }
  if (!anim) return base;

  anim.t += dt / TURN_DURATION;
  const { axis, dir } = anim.step;
  if (anim.t >= 1) {
    const full = axis === "x" ? rotationX((dir * Math.PI) / 2) : rotationY((dir * Math.PI) / 2);
    base = snap(multiply(full, base));
    const done = anim.done;
    anim = null;
    done?.();
    return base;
  }
  const angle = easeInOutCubic(anim.t) * (Math.PI / 2) * dir;
  const spin = axis === "x" ? rotationX(angle) : rotationY(angle);
  return multiply(spin, base);
}

function resize(): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = Math.floor(canvas.clientWidth * dpr);
  const h = Math.floor(canvas.clientHeight * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
}

gl.enable(gl.DEPTH_TEST);
gl.enable(gl.CULL_FACE);
gl.clearColor(0, 0, 0, 0); // transparent — the page provides the sky gradient

let acc = 0;

let lastTime = performance.now();

function frame(now: number): void {
  const dt = Math.min((now - lastTime) / 1000, 0.1);
  lastTime = now;

  // The press-animation clock; on clip end the shell fires the item's action
  // (which may swap `session` or start a turn before the sim steps below).
  shell?.update(dt);

  const live = session;
  if (live === null) {
    requestAnimationFrame(frame);
    return;
  }

  // Clamped above, so a tab suspend costs at most 0.1 s of catch-up steps.
  // The simulation runs on a fixed timestep, decoupled from the render frame
  // rate; rendering reads the latest sim state each animation frame.
  acc += dt;
  const drained = drainAccumulator(acc, SIM_DT);
  acc = drained.rest;
  for (let i = 0; i < drained.steps; i++) stepWorld(live.world);
  live.rebuildIfDirty();

  resize();
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

  const cam = frameCube(canvas.width / canvas.height);
  const proj = perspective(cam.fovY, cam.aspect, 0.1, 100);
  const view = translation(0, 0, -cam.dist);
  const viewProj = multiply(proj, view);
  model = currentModel(dt);
  const mvp = multiply(viewProj, model);

  const cube: Mesh = live.cube;
  gl.useProgram(program);
  gl.uniformMatrix4fv(uMvp, false, mvp);
  gl.uniformMatrix4fv(uModel, false, model);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, atlasTex);
  gl.bindVertexArray(cube.vao);
  gl.drawElements(gl.TRIANGLES, cube.indexCount, gl.UNSIGNED_SHORT, 0);

  // Bugs are advanced by the fixed-step sim above; the renderer only reads.
  if (bugRenderer) bugRenderer.draw(gl, modelRenderer, live.world.bugs.bugs, model, viewProj);
  if (spawnerRenderer) {
    spawnerRenderer.draw(gl, modelRenderer, live.world.spawners.spawners, model, viewProj, now / 1000);
  }
  if (powerUpRenderer) {
    powerUpRenderer.draw(gl, modelRenderer, live.world.powerups.powerups, model, viewProj, now / 1000);
  }
  if (roster && models) {
    const apple = models.apple;
    modelRenderer.draw(
      gl,
      apple,
      poseWorlds(apple, "idle", now / 1000, true),
      multiply(model, placeOnSurface(poseToWorld(ROSTER_APPLE), "apple", 1)),
      viewProj,
    );
  }
  if (powerups && models) {
    for (let slot = 0; slot < POWERUP_NAMES.length; slot++) {
      const name = POWERUP_NAMES[slot];
      const plant = models[name];
      modelRenderer.draw(
        gl,
        plant,
        poseWorlds(plant, "idle", now / 1000, true),
        multiply(model, placeOnSurface(poseToWorld(POWERUP_SPAWNS[slot]), name, 1)),
        viewProj,
      );
    }
  }

  // The menu pages and title, riding the cube's model matrix; drawn until the
  // shell reaches `running` (the fade overlay covers the `starting` swap).
  if (shell !== null && shell.state.kind !== "running" && menuRenderer !== null) {
    menuRenderer.draw(gl, modelRenderer, shell.state, model, viewProj);
  }

  // Ghost shells last, blended. Depth *test* stays on so shells behind solid
  // blocks are hidden, but depth *writes* are off so overlapping shells do not
  // cull each other. Ghost-on-ghost blending order is therefore wrong from some
  // angles; at <= 125 shells that is not worth a per-face sort, so do not
  // "fix" it by sorting — it is a deliberate trade.
  const ghosts: Mesh = live.ghosts;
  if (ghosts.indexCount > 0) {
    gl.useProgram(ghostProgram);
    gl.uniformMatrix4fv(uGhostMvp, false, mvp);
    gl.uniformMatrix4fv(uGhostModel, false, model);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE); // a shell is hollow; both sides should show
    gl.bindVertexArray(ghosts.vao);
    gl.drawElements(gl.TRIANGLES, ghosts.indexCount, gl.UNSIGNED_SHORT, 0);
    gl.enable(gl.CULL_FACE);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
  }

  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);
